/**
 * SalesLocationTable — tableau drill-down géographique pays → région → ville.
 *
 * Props :
 *   from  : 'YYYY-MM-DD' (borne incluse)
 *   to    : 'YYYY-MM-DD' (borne exclusive, déjà traduite)
 */

import { useState } from 'react';
import { ChevronRight, ChevronDown, ShoppingBag } from 'lucide-react';
import { useSalesByLocation } from '../hooks/useSalesAnalytics.js';
import { formatCurrency, flagEmoji } from '../lib/format.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function pct(part, total) {
  if (!total || total === 0) return 0;
  return (part / total) * 100;
}

function pctLabel(part, total) {
  const p = pct(part, total);
  if (!total || total === 0) return '—';
  return `${p.toFixed(1)} %`;
}

function formatBasket(cents) {
  if (cents === null || cents === undefined) return '—';
  return formatCurrency(cents);
}

// ── Barre % inline ────────────────────────────────────────────────────────────

/**
 * PartBar — barre de fond colorée qui indique la proportion visuellement.
 * Rendu via un élément de fond absolu (couleur primaire translucide) + texte par dessus.
 */
function PartBar({ partCents, totalCents, className = '' }) {
  const ratio = totalCents > 0 ? Math.min(pct(partCents, totalCents) / 100, 1) : 0;
  const pctText = pctLabel(partCents, totalCents);

  return (
    <div className={`relative flex items-center justify-end ${className}`}>
      {/* Barre de fond */}
      <div
        className="absolute inset-0 bg-blue-100 rounded-sm origin-right transition-all duration-500"
        style={{ transform: `scaleX(${ratio})`, transformOrigin: 'right' }}
        aria-hidden
      />
      {/* Texte */}
      <span className="relative z-10 tabular-nums text-xs font-medium text-slate-700 pr-1">
        {pctText}
      </span>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function TableSkeleton({ rows = 5 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-b border-slate-100">
          <td className="px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-4 bg-slate-100 animate-pulse rounded" />
              <div className="h-3.5 bg-slate-100 animate-pulse rounded w-24" />
            </div>
          </td>
          <td className="px-4 py-3">
            <div className="h-3.5 bg-slate-100 animate-pulse rounded w-16 ml-auto" />
          </td>
          <td className="px-4 py-3">
            <div className="h-3.5 bg-slate-100 animate-pulse rounded w-8 ml-auto" />
          </td>
          <td className="px-4 py-3 hidden sm:table-cell">
            <div className="h-3.5 bg-slate-100 animate-pulse rounded w-16 ml-auto" />
          </td>
          <td className="px-4 py-3">
            <div className="h-3.5 bg-slate-100 animate-pulse rounded w-12 ml-auto" />
          </td>
        </tr>
      ))}
    </>
  );
}

// ── Ligne "pays" ──────────────────────────────────────────────────────────────

function CountryRow({ row, totalCents, from, to, isExpanded, onToggle }) {
  const isUnknown = row.key === 'unknown';
  const flag = flagEmoji(row.key);

  return (
    <>
      <tr
        className={`border-b border-slate-100 transition-colors ${
          isUnknown
            ? 'cursor-not-allowed opacity-60'
            : 'hover:bg-blue-50/40 cursor-pointer'
        }`}
        onClick={isUnknown ? undefined : onToggle}
        aria-expanded={isUnknown ? undefined : isExpanded}
      >
        {/* Localisation */}
        <td className="px-4 py-3 sticky left-0 bg-white group-hover:bg-blue-50/40 transition-colors min-w-[180px]">
          <div className="flex items-center gap-2 min-w-0">
            {/* Chevron ou placeholder */}
            <span className="shrink-0 w-4 flex items-center justify-center">
              {isUnknown ? (
                <span className="w-4" />
              ) : isExpanded ? (
                <ChevronDown size={14} className="text-blue-500" />
              ) : (
                <ChevronRight size={14} className="text-slate-400" />
              )}
            </span>

            {/* Drapeau */}
            <span className="shrink-0 text-base leading-none" aria-hidden>{flag}</span>

            {/* Nom */}
            <span
              className={`text-sm font-semibold truncate ${
                isUnknown ? 'italic text-slate-400' : 'text-slate-900'
              }`}
            >
              {isUnknown ? 'Adresse inconnue' : row.label}
            </span>
          </div>
        </td>

        {/* CA */}
        <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold text-slate-900 whitespace-nowrap">
          {formatCurrency(row.ca_cents)}
        </td>

        {/* Commandes */}
        <td className="px-4 py-3 text-right tabular-nums text-sm text-slate-600 whitespace-nowrap">
          {row.orders_count}
        </td>

        {/* Panier moyen — masqué en mobile */}
        <td className="px-4 py-3 text-right tabular-nums text-sm text-slate-600 whitespace-nowrap hidden sm:table-cell">
          {formatBasket(row.average_basket_cents)}
        </td>

        {/* Part % avec barre visuelle — masquée sur mobile (on garde juste la barre) */}
        <td className="px-2 py-3 w-28 hidden sm:table-cell">
          <PartBar partCents={row.ca_cents} totalCents={totalCents} />
        </td>

        {/* Sur mobile : petite barre seule, compacte */}
        <td className="px-2 py-3 w-16 sm:hidden">
          <PartBar partCents={row.ca_cents} totalCents={totalCents} />
        </td>
      </tr>

      {/* Sous-lignes provinces si expanded */}
      {isExpanded && !isUnknown && (
        <ProvinceRows
          countryKey={row.key}
          countryLabel={row.label}
          countryTotal={row.ca_cents}
          from={from}
          to={to}
        />
      )}
    </>
  );
}

// ── Sous-lignes provinces ─────────────────────────────────────────────────────

function ProvinceRows({ countryKey, countryLabel, countryTotal, from, to }) {
  const { data, isLoading, error } = useSalesByLocation({
    from,
    to,
    level: 'province',
    country: countryKey,
  });

  const [expanded, setExpanded] = useState({});

  function toggleProvince(key) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  if (isLoading) {
    return (
      <tr>
        <td colSpan={5} className="p-0">
          <table className="w-full">
            <tbody>
              <TableSkeleton rows={3} />
            </tbody>
          </table>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td colSpan={5} className="px-4 py-2 bg-slate-50">
          <p className="pl-10 text-xs text-red-600">Impossible de charger les régions.</p>
        </td>
      </tr>
    );
  }

  const rows = Array.isArray(data) ? data : [];

  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={5} className="px-4 py-2 bg-slate-50">
          <p className="pl-10 text-xs text-slate-400 italic">Pas de données de région.</p>
        </td>
      </tr>
    );
  }

  return (
    <>
      {rows.map((prov) => {
        const isUnknown = prov.key === 'unknown';
        const isProvExpanded = Boolean(expanded[prov.key]);
        return (
          <>
            <tr
              key={`prov-${prov.key}`}
              className={`border-b border-slate-100 bg-slate-50/70 transition-colors ${
                isUnknown
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:bg-blue-50/30 cursor-pointer'
              }`}
              onClick={isUnknown ? undefined : () => toggleProvince(prov.key)}
              aria-expanded={isUnknown ? undefined : isProvExpanded}
            >
              <td className="px-4 py-2.5 sticky left-0 bg-slate-50/70 transition-colors">
                <div className="flex items-center gap-1.5 min-w-0 pl-6">
                  {/* Indentation + ligne verticale de profondeur */}
                  <span className="shrink-0 border-l-2 border-blue-200 h-4 mr-0.5" />
                  <span className="shrink-0 w-3.5 flex items-center justify-center">
                    {isUnknown ? (
                      <span className="w-3.5" />
                    ) : isProvExpanded ? (
                      <ChevronDown size={12} className="text-blue-400" />
                    ) : (
                      <ChevronRight size={12} className="text-slate-400" />
                    )}
                  </span>
                  <span
                    className={`text-xs font-medium truncate ${
                      isUnknown ? 'italic text-slate-400' : 'text-slate-700'
                    }`}
                  >
                    {isUnknown ? 'Région inconnue' : prov.label}
                  </span>
                </div>
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-xs font-semibold text-slate-800 whitespace-nowrap">
                {formatCurrency(prov.ca_cents)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-xs text-slate-600 whitespace-nowrap">
                {prov.orders_count}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-xs text-slate-600 whitespace-nowrap hidden sm:table-cell">
                {formatBasket(prov.average_basket_cents)}
              </td>
              <td className="px-2 py-2.5 w-28 hidden sm:table-cell">
                <PartBar partCents={prov.ca_cents} totalCents={countryTotal} />
              </td>
              <td className="px-2 py-2.5 w-16 sm:hidden">
                <PartBar partCents={prov.ca_cents} totalCents={countryTotal} />
              </td>
            </tr>

            {/* Villes si province étendue */}
            {isProvExpanded && !isUnknown && (
              <CityRows
                key={`cities-${prov.key}`}
                countryKey={countryKey}
                provinceKey={prov.key}
                provinceTotal={prov.ca_cents}
                from={from}
                to={to}
              />
            )}
          </>
        );
      })}
    </>
  );
}

// ── Sous-lignes villes ────────────────────────────────────────────────────────

function CityRows({ countryKey, provinceKey, provinceTotal, from, to }) {
  const { data, isLoading, error } = useSalesByLocation({
    from,
    to,
    level: 'city',
    country: countryKey,
    province: provinceKey,
  });

  if (isLoading) {
    return (
      <tr>
        <td colSpan={5} className="p-0">
          <table className="w-full">
            <tbody>
              <TableSkeleton rows={2} />
            </tbody>
          </table>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td colSpan={5} className="px-4 py-2 bg-slate-100">
          <p className="pl-16 text-xs text-red-600">Impossible de charger les villes.</p>
        </td>
      </tr>
    );
  }

  const rows = Array.isArray(data) ? data : [];

  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={5} className="px-4 py-2 bg-slate-100">
          <p className="pl-16 text-xs text-slate-400 italic">Pas de données de ville.</p>
        </td>
      </tr>
    );
  }

  return rows.map((city) => {
    const isUnknown = city.key === 'unknown';
    return (
      <tr key={`city-${city.key}`} className="border-b border-slate-100 bg-slate-100/80">
        <td className="px-4 py-2 sticky left-0 bg-slate-100/80">
          <div className="flex items-center gap-1.5 min-w-0 pl-10">
            {/* Double indentation + ligne verticale plus marquée */}
            <span className="shrink-0 border-l-2 border-violet-200 h-4 mr-0.5" />
            <span className="w-1.5 shrink-0" />
            <span
              className={`text-xs truncate ${
                isUnknown ? 'italic text-slate-400' : 'text-slate-600'
              }`}
            >
              {isUnknown ? 'Ville inconnue' : city.label}
            </span>
          </div>
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-xs text-slate-700 whitespace-nowrap">
          {formatCurrency(city.ca_cents)}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-xs text-slate-600 whitespace-nowrap">
          {city.orders_count}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-xs text-slate-600 whitespace-nowrap hidden sm:table-cell">
          {formatBasket(city.average_basket_cents)}
        </td>
        <td className="px-2 py-2 w-28 hidden sm:table-cell">
          <PartBar partCents={city.ca_cents} totalCents={provinceTotal} />
        </td>
        <td className="px-2 py-2 w-16 sm:hidden">
          <PartBar partCents={city.ca_cents} totalCents={provinceTotal} />
        </td>
      </tr>
    );
  });
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function SalesLocationTable({ from, to }) {
  const { data, isLoading, error } = useSalesByLocation({ from, to, level: 'country' });
  const [expanded, setExpanded] = useState({});

  function toggleCountry(key) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  const rows = Array.isArray(data) ? data : [];
  const totalCents = rows.reduce((s, r) => s + (r.ca_cents ?? 0), 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* En-tête */}
      <div className="px-4 py-3.5 border-b border-slate-100 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Ventilation géographique</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Cliquer sur un pays pour voir les régions et les villes.
          </p>
        </div>
        {!isLoading && rows.length > 0 && (
          <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full shrink-0 mt-0.5">
            {rows.length} pays
          </span>
        )}
      </div>

      {/* Erreur */}
      {error && (
        <div className="px-4 py-4">
          <p className="text-sm text-red-600">
            Impossible de charger la ventilation géographique : {error.message}
          </p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-10">
          <ShoppingBag size={32} className="text-slate-200" />
          <p className="text-sm text-slate-400 font-medium">Aucune commande sur cette période.</p>
          <p className="text-xs text-slate-300">Essaie une période plus large.</p>
        </div>
      )}

      {/* Tableau */}
      {!error && (isLoading || rows.length > 0) && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3 sticky left-0 bg-slate-50 whitespace-nowrap min-w-[180px]">
                  Localisation
                </th>
                <th className="text-right text-xs font-semibold text-slate-500 px-4 py-3 whitespace-nowrap">
                  CA TTC
                </th>
                <th className="text-right text-xs font-semibold text-slate-500 px-4 py-3 whitespace-nowrap">
                  Cmdtes
                </th>
                <th className="text-right text-xs font-semibold text-slate-500 px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                  Panier moy.
                </th>
                <th className="text-right text-xs font-semibold text-slate-500 px-2 py-3 whitespace-nowrap w-28 hidden sm:table-cell">
                  Part
                </th>
                <th className="text-right text-xs font-semibold text-slate-500 px-2 py-3 whitespace-nowrap w-16 sm:hidden">
                  Part
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableSkeleton rows={5} />
              ) : (
                rows.map((row) => (
                  <CountryRow
                    key={row.key}
                    row={row}
                    totalCents={totalCents}
                    from={from}
                    to={to}
                    isExpanded={Boolean(expanded[row.key])}
                    onToggle={() => toggleCountry(row.key)}
                  />
                ))
              )}
            </tbody>

            {/* Total footer */}
            {!isLoading && rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td className="px-4 py-3 sticky left-0 bg-slate-50 text-xs font-bold text-slate-700 uppercase tracking-wide">
                    Total
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-sm font-bold text-slate-900 whitespace-nowrap">
                    {formatCurrency(totalCents)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-xs font-semibold text-slate-700 whitespace-nowrap">
                    {rows.reduce((s, r) => s + (r.orders_count ?? 0), 0)}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell" />
                  <td className="px-2 py-3 text-right text-xs font-semibold text-slate-500 whitespace-nowrap hidden sm:table-cell">
                    100 %
                  </td>
                  <td className="px-2 py-3 text-right text-xs font-semibold text-slate-500 whitespace-nowrap sm:hidden">
                    100 %
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
